import classNames from "classnames"
import PropTypes from "prop-types"
import React from "react"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"

const ListedPumpPrices = ({
  fuel,
  marinaName,
  recentRequestActive,
  fuelDockAvailabilityEnabled,
  handleRequestPricesModalOpen,
}) => {
  const { fuelMeasurementUnitSymbol } = useMarina()

  const RequestButton = () => (
    <Button
      variant="primary"
      onClick={() => handleRequestPricesModalOpen({ fuelPricesPresent: false })}
    >
      Request Fuel Prices
    </Button>
  )

  const RequestSentMessage = () => (
    <span className="text-base font-semibold text-teal-600">
      Your request for updated prices has been sent! Check back soon.
    </span>
  )

  const NoPricesFuelPriceRequestCta = () => (
    <div
      className={classNames(
        "flex h-48 flex-col items-center justify-center space-y-4 text-center",
        { "h-full rounded bg-gray-100 p-4": fuelDockAvailabilityEnabled }
      )}
    >
      <span
        className={classNames("w-full text-center text-base font-semibold", {
          "w-2/3": !fuelDockAvailabilityEnabled,
          "w-full": fuelDockAvailabilityEnabled,
        })}
      >
        {marinaName} does not have any listed fuel pump prices available at this
        time
      </span>
      {recentRequestActive ? <RequestSentMessage /> : <RequestButton />}
    </div>
  )

  const FuelPriceItem = ({ fuelItem }) => {
    const showDiscount = fuelItem.discounted

    return (
      <div className="col-span-2 flex flex-col space-y-1 md:col-span-1">
        <span className="text-sm font-semibold">{fuelItem.name}</span>
        <span className="text-base font-bold">
          {fuelItem.priceDollars}/{fuelMeasurementUnitSymbol}
        </span>
        {showDiscount && (
          <div className="flex items-center space-x-1 text-sm font-semibold text-supplementary-orange-600">
            <i className="fa-kit fa-tag-outline text-lg" aria-hidden="true" />
            <span>
              {fuelItem.effectivePriceDollars}/{fuelMeasurementUnitSymbol} (
              {fuelItem.readableDiscount})
            </span>
          </div>
        )}
      </div>
    )
  }

  FuelPriceItem.propTypes = {
    fuelItem: PropTypes.object.isRequired,
  }

  const FuelPricesList = () => (
    <div
      className={classNames({
        "flex flex-col space-y-2": fuelDockAvailabilityEnabled,
        "grid w-full grid-cols-2 gap-4": !fuelDockAvailabilityEnabled,
      })}
    >
      {fuel.map((fuelItem, index) => (
        <FuelPriceItem key={index} fuelItem={fuelItem} />
      ))}
    </div>
  )

  FuelPricesList.propTypes = {
    fuel: PropTypes.array,
  }

  const hasFuelPrices = fuel.length > 0

  return (
    <div className="flex w-full" id="listed-pump-prices">
      {hasFuelPrices ? <FuelPricesList /> : <NoPricesFuelPriceRequestCta />}
    </div>
  )
}

ListedPumpPrices.propTypes = {
  fuel: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      priceDollars: PropTypes.string.isRequired,
      effectivePriceDollars: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      discounted: PropTypes.bool.isRequired,
      readableDiscount: PropTypes.string,
    })
  ),
  marinaName: PropTypes.string.isRequired,
  recentRequestActive: PropTypes.bool.isRequired,
  fuelDockAvailabilityEnabled: PropTypes.bool.isRequired,
  handleRequestPricesModalOpen: PropTypes.func.isRequired,
}

export default ListedPumpPrices
